// fetchEvent.jsx - API utility functions for Event management

// Base configuration
const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Status mapping from Hebrew to English
const STATUS_MAPPING = {
    'ממתין': 'pending',
    'מאושר': 'confirmed',
    'מבוטל': 'cancelled',
    'הסתיים': 'completed'
};

// Reverse mapping from English to Hebrew
const REVERSE_STATUS_MAPPING = {
    'pending': 'ממתין',
    'confirmed': 'מאושר',
    'cancelled': 'מבוטל',
    'completed': 'הסתיים'
};

// Function to convert Hebrew status to English
const mapStatusToEnglish = (hebrewStatus) => {
    return STATUS_MAPPING[hebrewStatus] || hebrewStatus;
};

// Function to convert English status to Hebrew
const mapStatusToHebrew = (englishStatus) => {
    return REVERSE_STATUS_MAPPING[englishStatus] || englishStatus;
};

// Default headers for all requests with token authentication
const getHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('No authentication token found');
    }

    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

// Error handling utility
const handleApiError = async (response) => {
    if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;

            // Handle validation errors
            if (response.status === 400 && errorData.errors) {
                const validationErrors = Object.values(errorData.errors).flat();
                errorMessage = validationErrors.join(', ');
            }
        } catch (parseError) {
            // If error response is not JSON, use the default message
        }

        throw new Error(errorMessage);
    }
    return response;
};

// Utility function to validate event data
const validateEventData = (eventData) => {
    const errors = [];

    if (!eventData.name || eventData.name.trim() === '') {
        errors.push('Event name is required');
    }

    if (!eventData.date || eventData.date.trim() === '') {
        errors.push('Date is required');
    }

    if (!eventData.time || eventData.time.trim() === '') {
        errors.push('Time is required');
    }

    if (!eventData.location || eventData.location.trim() === '') {
        errors.push('Location is required');
    }

    if (!eventData.createdBy || eventData.createdBy.trim() === '') {
        errors.push('Created by is required');
    }

    if (eventData.confirmUsersNumber < 0) {
        errors.push('Confirmed users number cannot be negative');
    }

    // Check both Hebrew and English status values
    const validEnglishStatuses = ['pending', 'confirmed', 'cancelled', 'completed'];
    const validHebrewStatuses = ['ממתין', 'מאושר', 'מבוטל', 'הסתיים'];

    if (!validEnglishStatuses.includes(eventData.status) && !validHebrewStatuses.includes(eventData.status)) {
        errors.push('Invalid status. Must be: ממתין, מאושר, מבוטל, or הסתיים');
    }

    if (errors.length > 0) {
        throw new Error(`Validation errors: ${errors.join(', ')}`);
    }
};

// Helper function to prepare event data for API (convert Hebrew to English)
const prepareEventDataForAPI = (eventData) => {
    return {
        ...eventData,
        status: mapStatusToEnglish(eventData.status)
    };
};

// Helper function to prepare event data for UI (convert English to Hebrew)
const prepareEventDataForUI = (eventData) => {
    if (Array.isArray(eventData)) {
        return eventData.map(event => ({
            ...event,
            status: mapStatusToHebrew(event.status)
        }));
    }
    return {
        ...eventData,
        status: mapStatusToHebrew(eventData.status)
    };
};

// GET: Fetch all events
export const listEvents = async (buildingId) => {
    try {

        const response = await fetch(`${API_URL}/api/Event/List/${buildingId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        });

        await handleApiError(response);
        const events = await response.json();

        // Convert English status to Hebrew for UI
        return prepareEventDataForUI(events);
    } catch (error) {
        throw new Error(`Failed to fetch events: ${error.message}`);
    }
};

// GET: Fetch a single event by ID
export const getEvent = async (eventId) => {
    try {
        if (!eventId) {
            throw new Error('Event ID is required');
        }

        const response = await fetch(`${API_URL}/api/Event/List/${eventId}`, {
            method: 'GET',
            headers: getHeaders(),
        });

        await handleApiError(response);
        const event = await response.json();

        // Convert English status to Hebrew for UI
        return prepareEventDataForUI(event);
    } catch (error) {
        throw new Error(`Failed to fetch event: ${error.message}`);
    }
};

// POST: Create a new event
export const createEvent = async (eventData) => {
    try {

        // Validate event data before sending
        validateEventData(eventData);

        // Prepare the data (exclude ID for creation and convert status to English)
        const { id, ...createData } = prepareEventDataForAPI(eventData);

        const response = await fetch(`${API_URL}/api/Event/Create`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(createData),
        });

        await handleApiError(response);
        const createdEvent = await response.json();

        // Convert English status to Hebrew for UI
        return prepareEventDataForUI(createdEvent);
    } catch (error) {
        throw new Error(`Failed to create event: ${error.message}`);
    }
};

// POST: Edit an existing event
export const editEvent = async (eventId, eventData) => {
    try {
        if (!eventId) {
            throw new Error('Event ID is required for editing');
        }


        // Validate event data before sending
        validateEventData(eventData);

        // Prepare the data (exclude ID from body since it's in the URL and convert status to English)
        const { id, ...updateData } = prepareEventDataForAPI(eventData);

        const response = await fetch(`${API_URL}/api/Event/Edit/${eventId}`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(updateData),
        });

        await handleApiError(response);
        const updatedEvent = await response.json();

        // Convert English status to Hebrew for UI
        return prepareEventDataForUI(updatedEvent);
    } catch (error) {
        throw new Error(`Failed to update event: ${error.message}`);
    }
};

// POST: Delete an event
export const deleteEvent = async (eventId) => {
    try {
        if (!eventId) {
            throw new Error('Event ID is required for deletion');
        }

        const response = await fetch(`${API_URL}/api/Event/Delete/${eventId}`, {
            method: 'POST',
            headers: getHeaders(),
        });

        await handleApiError(response);
        const result = await response.json();

        return result;
    } catch (error) {
        throw new Error(`Failed to delete event: ${error.message}`);
    }
};

// Alternative DELETE method (if your backend supports it)
export const deleteEventAlternative = async (eventId) => {
    try {
        if (!eventId) {
            throw new Error('Event ID is required for deletion');
        }


        const response = await fetch(`${API_URL}/${eventId}`, {
            method: 'DELETE',
            headers: getHeaders(),
        });

        await handleApiError(response);
        const result = await response.json();

        return result;
    } catch (error) {
        throw new Error(`Failed to delete event: ${error.message}`);
    }
};

// Utility function to get authentication token (if using authentication)
const getAuthToken = () => {
    return localStorage.getItem('token') || '';
};

// Utility function to set authentication token
export const setAuthToken = (token) => {
    localStorage.setItem('token', token);
};

// Utility function to clear authentication token
export const clearAuthToken = () => {
    localStorage.removeItem('token');
};

// Advanced: Batch operations
export const batchDeleteEvents = async (eventIds) => {
    try {

        const deletePromises = eventIds.map(id => deleteEvent(id));
        const results = await Promise.allSettled(deletePromises);

        const successful = results.filter(result => result.status === 'fulfilled');
        const failed = results.filter(result => result.status === 'rejected');


        return {
            successful: successful.length,
            failed: failed.length,
            results: results
        };
    } catch (error) {
        throw new Error(`Failed to batch delete events: ${error.message}`);
    }
};

// Advanced: Search and filter events
export const searchEvents = async (searchParams) => {
    try {

        const queryParams = new URLSearchParams();

        // Add search parameters (convert status to English if provided)
        if (searchParams.name) {
            queryParams.append('name', searchParams.name);
        }
        if (searchParams.status) {
            queryParams.append('status', mapStatusToEnglish(searchParams.status));
        }
        if (searchParams.date) {
            queryParams.append('date', searchParams.date);
        }
        if (searchParams.location) {
            queryParams.append('location', searchParams.location);
        }
        if (searchParams.createdBy) {
            queryParams.append('createdBy', searchParams.createdBy);
        }

        const response = await fetch(`${API_URL}/search?${queryParams.toString()}`, {
            method: 'GET',
            headers: getHeaders(),
        });

        await handleApiError(response);
        const events = await response.json();

        // Convert English status to Hebrew for UI
        return prepareEventDataForUI(events);
    } catch (error) {
        throw new Error(`Failed to search events: ${error.message}`);
    }
};